import { Box, Button, ButtonGroup, Divider, Grid, IconButton, Menu, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@material-ui/core";
import { FilterList } from "@material-ui/icons";
import { Pagination } from '@material-ui/lab';
import moment from 'moment';
import React from 'react';
import { useHistory } from "react-router-dom";
import SearchField from "../../components/SearchField";
import Page from "../../containers/Page/Page";
import { usePermissions } from '../../hooks/permissions';
import { api } from '../../services/api';
import { Helpers } from '../../utils/Helpers';

interface IData {
    data: {
        id: string;
        ultima_versao: string;
        titulo: string;
        emitente: string;
        cliente_nome: string;
        segmentonome: string;
        congelado: boolean;
        finalizado: boolean;
        created_at: string;
        ras: string;
    }[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

function Propostas() {

    //Hooks
    const history = useHistory();
    const permissions = usePermissions();
    const query = new URLSearchParams(history.location.search);

    //States
    const [error, setError] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);
    const [search, setSearch] = React.useState<string>(query.get('search') || '');
    const [data, setData] = React.useState<IData>();
    const [filterMenu, setFilterMenu] = React.useState<null | HTMLElement>(null);
    const [emitenteFilter, setEmitenteFilter] = React.useState<string>(query.get('emitente') || 'todos');
    /**
     * Ao carregar a página.
     */
    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Carregar propostas.
     * @param page 
     */
    async function handleFetch(page: number = Number(query.get('page')) || 1) {
        setLoading(true);
        setFilterMenu(null);
        try {
            let url = `/propostas?search=${search}&page=${page}`;
            url += emitenteFilter ? `&emitente=${emitenteFilter}` : '';
            const response = await api.get(url);
            history.push(url);
            setData(response.data.object);
            setError('');
        } catch (error: any) {
            setError(Helpers.errorFormat(error));
        }
        setLoading(false);
    }

    /**
     * Pesquisar com ENTER.
     * @param event 
     */
    function handleSearchKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (event.key === 'Enter') {
            handleFetch(1);
        }
    }

    return (
        <Page error={error} loading={loading}>
            <Page.Title>
                <Typography variant="h6" component="div">
                    Propostas
                </Typography>
                {permissions.has('proposta.create') &&
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => history.push('/propostas/create')}>
                        Nova Proposta
                    </Button>
                }
            </Page.Title>
            {data &&
                <Page.Section>
                    <Page.Toolbar>
                        {/* <FormControlLabel control={<Switch />} label="Somente Minhas" /> */}
                        <SearchField
                            placeholder="Pesquisa"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyUp={(event) => handleSearchKeyUp(event)}
                            onClick={() => handleFetch(1)}
                        />
                        <Box ml={2}>
                            <Tooltip arrow={true} placement="top" title="Filtros">
                                <IconButton onClick={(event) => setFilterMenu(event.currentTarget)}>
                                    <FilterList />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Menu
                            id="filter-menu"
                            anchorEl={filterMenu}
                            keepMounted
                            open={Boolean(filterMenu)}
                            onClose={() => setFilterMenu(null)}
                        >
                            <Box p={2} width={300}>
                                <Box mb={2}>
                                    <Typography variant="h6" component="h6">
                                        Filtros
                                    </Typography>
                                </Box>
                                <Box mb={2}>
                                    <Divider />
                                </Box>
                                <Box mb={2}>
                                    <Box mb={2}>
                                        <Typography variant="body1" component="p" style={{ fontWeight: 'bold' }}>
                                            EMITENTE
                                        </Typography>
                                    </Box>
                                    <ButtonGroup size="small" color="primary" fullWidth={true} >
                                        <Button
                                            variant={emitenteFilter === 'todos' ? 'contained' : 'outlined'}
                                            onClick={() => setEmitenteFilter('todos')}>
                                            Todos
                                        </Button>
                                        <Button
                                            variant={emitenteFilter === 'minhas' ? 'contained' : 'outlined'}
                                            onClick={() => setEmitenteFilter('minhas')}>
                                            Minhas
                                        </Button>
                                    </ButtonGroup>
                                </Box>
                                <Box mb={2}>
                                    <Divider />
                                </Box>
                                <Box display="flex" justifyContent="flex-end">
                                    <Button onClick={() => handleFetch(1)} variant="contained">
                                        Filtrar
                                    </Button>
                                </Box>
                            </Box>
                        </Menu>
                    </Page.Toolbar>
                    <Page.Content>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Num. Prop</TableCell>
                                        <TableCell>Título do Projeto</TableCell>
                                        <TableCell>Emitente</TableCell>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell>Segmento</TableCell>
                                        <TableCell>Congelado</TableCell>
                                        <TableCell>Finalizado</TableCell>
                                        <TableCell>Data Criação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.data.map((proposta) => (
                                        <TableRow
                                            key={proposta.id}
                                            hover={true}
                                            onClick={() => history.push(`/propostas/${proposta.ultima_versao}`)}
                                        >
                                            <TableCell>{proposta.ras}</TableCell>
                                            <TableCell>{Helpers.trim(proposta.titulo, 45)}</TableCell>
                                            <TableCell>{proposta.emitente}</TableCell>
                                            <TableCell>{proposta.cliente_nome}</TableCell>
                                            <TableCell>{proposta.segmentonome}</TableCell>
                                            <TableCell>{proposta.congelado ? 'Sim' : 'Não'}</TableCell>
                                            <TableCell>{proposta.finalizado ? 'Sim' : 'Não'}</TableCell>
                                            <TableCell>{moment(proposta.created_at).format('DD/MM/YYYY')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box p={2}>
                            <Grid container justifyContent="center">
                                <Pagination
                                    page={data.current_page}
                                    count={data.last_page}
                                    onChange={(_event, page) => handleFetch(page)}
                                    color="secondary"
                                />
                            </Grid>
                        </Box>
                    </Page.Content>
                </Page.Section>
            }
        </Page >
    );
}

export default Propostas;